import type { Piece, PieceStatus } from '@miraki/contracts';
import { ApiError } from '../../api/client';
import { addMoney } from './line-money';

/**
 * Read-side rules for R-20 My Inventory and R-21 Sold Items, kept pure so they can be tested
 * without a browser. Nothing here computes a figure the API owns:
 *
 *   - `daysHeld` is the API's, ONE definition (D80): days the retailer held the piece, ending at
 *     the sale for a sold piece and at today for a held one. It is displayed, filtered and
 *     averaged here, never derived. There is no `receivedAt → today` arithmetic in this file.
 *   - the budget is `ConsignmentService.budget()`'s, read through R-01's route.
 *   - money is summed only within one currency, as decimal strings (AN-08, NF-04).
 */

// ------------------------------------------------------------------ not yet

/**
 * The consignment routes answer 501 `{ code: 'NOT_YET', waitingFor }` while the service behind
 * them still throws `WS-1a` (hand-over item 1). That, and only that, is "not built yet". Every
 * other failure — a 404, a 403, a 500 — is a real error and is shown as one: an unbuilt feature
 * and a broken one must never look alike.
 */
export interface NotYet { readonly waitingFor: string }

export function notYet(err: unknown): NotYet | null {
  if (!(err instanceof ApiError) || err.status !== 501) return null;
  const d = err.detail as { code?: unknown; waitingFor?: unknown } | undefined;
  return d?.code === 'NOT_YET' ? { waitingFor: typeof d.waitingFor === 'string' ? d.waitingFor : '' } : null;
}

// ------------------------------------------------------------------ where a piece belongs

/**
 * Which part of which screen shows a piece, by state. Exhaustive over the contract's states, so
 * a state added to `zPieceStatus` fails to compile here until someone says where it goes.
 *
 *   HELD       R-20's list. The only state that can be marked sold or put on a return request.
 *   ON_THE_WAY R-20, a separate section (CN-03, D56): in transit, or in DISCREPANCY at receipt.
 *              Not held yet, so no days held and no actions.
 *   REPORTED   R-20, a separate section: reported lost or damaged, outcome not yet recorded
 *              (CN-10). Not held and not sold; shown so a piece does not silently vanish.
 *   SOLD       R-21 — sold, invoiced or paid. WHICH of the three is debt detail, and DB-07 keeps
 *              that from a user without `shop.ledger`; see `paymentStateVisible`.
 *   GONE       Neither screen. Two different reasons share it: the piece left the retailer
 *              without being sold (returned, charged, claimed, written off), or it never reached
 *              them at all (MADE — D77, a finished piece still at Miraki). Both answer the only
 *              question this map asks, "where on R-20 or R-21 does it go", with "nowhere".
 */
export type Placement = 'HELD' | 'ON_THE_WAY' | 'REPORTED' | 'SOLD' | 'GONE';

export const PLACEMENT: Readonly<Record<PieceStatus, Placement>> = {
  MADE: 'GONE',
  HELD: 'HELD',
  IN_TRANSIT: 'ON_THE_WAY', DISCREPANCY: 'ON_THE_WAY',
  LOST: 'REPORTED', DAMAGED: 'REPORTED',
  SOLD: 'SOLD', INVOICED: 'SOLD', PAID: 'SOLD',
  RETURNED: 'GONE', CHARGED: 'GONE', INSURANCE_CLAIM: 'GONE', WRITTEN_OFF: 'GONE',
};

/**
 * A state this map does not list is not shown rather than crashing the screen. The gap it was
 * written for is closed — D77's MADE is in `zPieceStatus` now, so the exhaustive Record above
 * catches it at compile time — and the null branch stays as the guard for the next state added
 * to the contract before this file is updated.
 */
export const placementOf = (status: string): Placement | null =>
  (PLACEMENT as Record<string, Placement | undefined>)[status] ?? null;

export function partition(pieces: readonly Piece[]) {
  const by: Record<Placement, Piece[]> = { HELD: [], ON_THE_WAY: [], REPORTED: [], SOLD: [], GONE: [] };
  for (const p of pieces) {
    const at = placementOf(p.status);
    if (at) by[at].push(p);
  }
  return by;
}

/** DB-07 — whether a sold piece's invoiced / paid state may be shown. */
export const paymentStateVisible = (permissions: readonly string[]) => permissions.includes('shop.ledger');

// ------------------------------------------------------------------ search and selection

/** SL-01 — search by Job Number or product name. Case-insensitive; Job Numbers match anywhere. */
export function search(pieces: readonly Piece[], q: string): Piece[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return [...pieces];
  return pieces.filter((p) => p.jobNumber.toLowerCase().includes(needle) || p.description.toLowerCase().includes(needle));
}

/** SL-02 — what will become payable, per currency. Never one number across currencies. */
export function wholesaleByCurrency(pieces: readonly Piece[]): Array<{ currency: string; amount: string }> {
  const totals = new Map<string, string>();
  for (const p of pieces) totals.set(p.currency, addMoney(totals.get(p.currency) ?? '0.0000', p.wholesaleValue));
  return [...totals.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, amount]) => ({ currency, amount }));
}

// ------------------------------------------------------------------ dates

/** CN-06 "days / months held" — the months are a reading of `daysHeld`, never a second count. */
export const monthsHeld = (daysHeld: number | null): number | null => (daysHeld === null ? null : Math.floor(daysHeld / 30));

/*
 * SL-04's bound now lives in `@miraki/contracts`, because the server enforces it and this screen
 * offers it — two declarations of one number, with nothing holding them together. Re-exported so
 * the screen's imports do not change.
 */
export { SALE_DATE_BACK_DAYS, saleDateBounds } from '@miraki/contracts';

// ------------------------------------------------------------------ SL-08

/**
 * SL-08 — which products sell best: pieces sold per product, and the average of their `daysHeld`.
 * The average is OF the one definition, not a new one: rounded to whole days.
 *
 * Grouped by `description`, because `zPiece` carries no product id. Two variants sharing a name
 * would be counted as one product — hand-over asks for `productCode` on the contract.
 */
export function bestSellers(sold: readonly Piece[]) {
  const by = new Map<string, { name: string; sold: number; days: number[] }>();
  for (const p of sold) {
    const g = by.get(p.description) ?? { name: p.description, sold: 0, days: [] };
    g.sold += 1;
    if (p.daysHeld !== null) g.days.push(p.daysHeld);
    by.set(p.description, g);
  }
  return [...by.values()]
    .map((g) => ({
      name: g.name, sold: g.sold,
      avgDaysToSale: g.days.length ? Math.round(g.days.reduce((a, b) => a + b, 0) / g.days.length) : null,
    }))
    .sort((a, b) => b.sold - a.sold || a.name.localeCompare(b.name));
}

/** Newest sale first; an undated sale (should not happen) last. */
export const bySaleDate = (a: Piece, b: Piece) => (b.soldOn ?? '').localeCompare(a.soldOn ?? '');
